import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Calendar, ChevronDown, Upload, Download, Eye, Trash2, X, Search, RefreshCw } from 'lucide-react';
import AgGrid from '../../components/common/AgGrid';
import Icon from '../common/Icon';
import SuccessMessage from '../../components/common/SuccessMessage';
import {
  GetJobDetailsbyUserId,
  UploadTalliesIntegration,
  GetTalliesBatches,
  DeleteTalliesRows,
  GetBatchDetails,
  SaveBatchData,
  UpdateBatchDetails
} from '../../services/accountService';
import * as XLSX from 'xlsx';
import ListOfList from './ListOfList';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasPermission, isListAgent, canViewField, isRestrictedUser } from '../../utils/PermissionUtils';
const canDeleteTallies = canViewField('deletetallies') && !isRestrictedUser();

const TALLIES_TEMPLATE_DATA = [
  {
    'Mailcode': 'C1025P01',
    'Donations': 10,
    'Nondonors': 5,
    'Amount': 250.00
  }
];


const EXPECTED_HEADERS = ['Mailcode', 'Donations', 'Nondonors', 'Amount'];


const boldStyle = {
  fontWeight: 700,
  fontSize: '14px',
  color: '#000',
  display: 'inline-block',
  width: '100%'
};


const isTotalsRow = (params) => {
  return params.node?.rowPinned === 'bottom' || params.data?.isTotalsRow;
};

const TalliesSidebar = () => {
  const location = useLocation();


  const [activeTab, setActiveTab] = useState(() => {
    if (location.state?.activeTab === 'lists') return 'lists';
    if (location.state?.activeTab === 'tallies') return 'tallies';
    return isListAgent() ? 'lists' : 'tallies';
  });

  const [uploadBatchDate, setUploadBatchDate] = useState('');
  const [uploadBatchNumber, setUploadBatchNumber] = useState('');
  const [viewBatchDate, setViewBatchDate] = useState('');
  const [viewBatchNumber, setViewBatchNumber] = useState('');
  const [returns, setReturns] = useState('Non-Cumulative');
  const [isDragging, setIsDragging] = useState(false);
  const [activeBatchTab, setActiveBatchTab] = useState('pending');

  const [showBatchDetails, setShowBatchDetails] = useState(true);
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
  const [hasSelectedBatch, setHasSelectedBatch] = useState(false);



  const passedBatchNumber = location.state?.batchNumber || '';
  const [mailCodeFilterContext, setMailCodeFilterContext] = useState(null);



  const [batchDateError, setBatchDateError] = useState('');
  const [batchNumberError, setBatchNumberError] = useState('');
  const [fileError, setFileError] = useState('');

  const fileInputRef = useRef(null);

  const canViewLists = hasPermission('LoL.View');
  const viewOnly = isListAgent();
  const canUploadTallies = canViewField('uploadtallies') && !isRestrictedUser();
  const canEditTallies = canViewField('edittallies') && !isRestrictedUser();
  const canDeleteTallies = canViewField('deletetallies') && !isRestrictedUser();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isManageMailCodesActive, setIsManageMailCodesActive] = useState(false);
  const [deletingMailCodes, setDeletingMailCodes] = useState(false);
  const [loadingListSummary, setLoadingListSummary] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedPendingClient, setSelectedPendingClient] = useState('');
  const [searchPendingBatchTerm, setSearchPendingBatchTerm] = useState('');
  const [searchSuccessfulBatchTerm, setSearchSuccessfulBatchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState(null);

  const navigate = useNavigate();


  const [pendingBatchData, setPendingBatchData] = useState([]);
  const [successfulBatchData, setSuccessfulBatchData] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchesError, setBatchesError] = useState(null);


  const [selectedMailCodeRows, setSelectedMailCodeRows] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);


  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteContext, setDeleteContext] = useState(null);



  const [mailCodeData, setMailCodeData] = useState([]);


  const passedClientName = location.state?.clientName;

  const getClientCodeById = (id) => {
    if (!id) return '';
    const client = clients.find(c => c.clientID === id);
    return client ? client.clientCode : '';
  };




  const [batchTotals, setBatchTotals] = useState(null);


  const [gridApi, setGridApi] = useState(null);
  const [hasNewRow, setHasNewRow] = useState(false);


  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);


  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');


  const getTodayDateForInput = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);


  const showSuccess = (message) => {
    setSuccessMessage(message);
  };

  const hideSuccess = () => {
    setSuccessMessage('');
  };

  const showError = (message) => {
    setErrorMessage(message);
  };

  const hideError = () => {
    setErrorMessage('');
  };

  const incomingBatchNumbers = location.state?.batchNumbers || [];

  useEffect(() => {

    const incomingBatchNumbers = location.state?.batchNumbers
      || (location.state?.batchNumber ? [location.state.batchNumber] : []);

    if (incomingBatchNumbers.length === 1) {
      setActiveBatchTab('successful');
      setSearchSuccessfulBatchTerm(incomingBatchNumbers[0]);
      setMailCodeFilterContext(null);
    } else if (incomingBatchNumbers.length > 1) {
      setActiveBatchTab('successful');
      setMailCodeFilterContext({
        mailCode: location.state?.mailCode || '',
        batchNumbers: new Set(incomingBatchNumbers.map(b => b.toString().toLowerCase().trim()))
      });
      setSearchSuccessfulBatchTerm('');
    }
  }, [location.state]);


  const isBatchSuccessful = useMemo(() => {
    if (!selectedBatchDetails) return false;
    return selectedBatchDetails.isPending === false;
  }, [selectedBatchDetails]);


  const isEditAllowed = useMemo(() => {
    return hasSelectedBatch && !isBatchSuccessful && canEditTallies;
  }, [hasSelectedBatch, isBatchSuccessful, canEditTallies]);


  useEffect(() => {
    fetchClients();
  }, []);


  useEffect(() => {
    const incomingBatchNumbers = location.state?.batchNumbers || [];

    if (incomingBatchNumbers.length === 1) {

      setActiveBatchTab('successful');
      setSearchSuccessfulBatchTerm(incomingBatchNumbers[0]);
      setMailCodeFilterContext(null);
    } else if (incomingBatchNumbers.length > 1) {

      setActiveBatchTab('successful');
      setMailCodeFilterContext({
        mailCode: location.state?.mailCode || '',
        batchNumbers: new Set(incomingBatchNumbers.map(b => b.toString().toLowerCase().trim()))
      });
      setSearchSuccessfulBatchTerm('');
    }
  }, [location.state]);



  useEffect(() => {
    fetchBatches(activeBatchTab);
  }, [activeBatchTab]);


  useEffect(() => {
    if (gridApi && isManageMailCodesActive) {
      const allRows = [];
      gridApi.forEachNode(node => {
        if (!node.data?.isAddRow && !node.data?.isNewRow && !node.data?.isTotalsRow) {
          allRows.push(node.data);
        }
      });

      const selectedCount = selectedMailCodeRows.length;
      const totalSelectableRows = allRows.length;

      if (selectedCount === 0) {
        setSelectAllChecked(false);
        setIsIndeterminate(false);
      } else if (selectedCount === totalSelectableRows && totalSelectableRows > 0) {
        setSelectAllChecked(true);
        setIsIndeterminate(false);
      } else {
        setSelectAllChecked(false);
        setIsIndeterminate(true);
      }
    }
  }, [selectedMailCodeRows, gridApi, isManageMailCodesActive]);


  useEffect(() => {

    handleCloseBatchDetails();
  }, [activeBatchTab]);


  const fetchClients = async () => {
    setLoadingClients(true);
    setClientsError(null);
    try {
      const response = await GetJobDetailsbyUserId();
      const activeClients = response.filter(client => client.isActive === true);
      setClients(activeClients);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClientsError("Failed to load clients. Please try again.");
    } finally {
      setLoadingClients(false);
    }
  };


  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  };


  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };


  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };


  const validateBatchDate = (date) => {
    if (!date) {
      setBatchDateError('Batch Date is required');
      return false;
    }

    const [year, month, day] = date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);

    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    setBatchDateError('');
    return true;
  };

  const validateBatchNumber = (number) => {
    if (!number || number.trim() === '') {
      setBatchNumberError('Batch Number is required');
      return false;
    }

    if (number.length > 50) {
      setBatchNumberError('Batch Number cannot exceed 50 characters');
      return false;
    }

    if (number.trim().length === 0) {
      setBatchNumberError('Batch Number cannot contain only spaces');
      return false;
    }

    const allowedCharsRegex = /^[a-zA-Z0-9_-]+$/;
    if (!allowedCharsRegex.test(number)) {
      setBatchNumberError('Batch Number can only contain letters, numbers, hyphens, and underscores');
      return false;
    }

    setBatchNumberError('');
    return true;
  };

  const validateFile = (file) => {
    setFileError('');

    if (!file) {
      setFileError('File is required');
      return false;
    }

    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError('File size must be less than 25MB');
      return false;
    }

    const isValidExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
    if (!isValidExcel) {
      setFileError('Invalid file format. Please upload Excel file (.xlsx or .xls)');
      return false;
    }

    return true;
  };

  const validateExcelFormat = (jsonData) => {
    if (!jsonData || jsonData.length === 0) {
      return { isValid: false, message: 'File must contain at least one data row' };
    }

    const firstRow = jsonData[0];
    const headers = Object.keys(firstRow);

    const missingHeaders = EXPECTED_HEADERS.filter(header => !headers.includes(header));

    if (missingHeaders.length > 0) {
      return {
        isValid: false,
        message: `Invalid format. Please use template for reference`
      };
    }

    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];

      if (!row['Mailcode'] || typeof row['Mailcode'] !== 'string') {
        return { isValid: false, message: `Row ${i + 1}: Mailcode must be a non-empty string` };
      }

      if (isNaN(Number(row['Donations'])) || Number(row['Donations']) < 0) {
        return { isValid: false, message: `Row ${i + 1}: Donations must be a positive number` };
      }

      if (isNaN(Number(row['Nondonors'])) || Number(row['Nondonors']) < 0) {
        return { isValid: false, message: `Row ${i + 1}: Nondonors must be a positive number` };
      }

      if (isNaN(Number(row['Amount'])) || Number(row['Amount']) < 0) {
        return { isValid: false, message: `Row ${i + 1}: Amount must be a positive number` };
      }
    }

    return { isValid: true, message: 'Valid format' };
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet(TALLIES_TEMPLATE_DATA);

    ws['!cols'] = [
      { wch: 15 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'tallies_template.xlsx');
  };


  const fetchBatches = async (status) => {
    setLoadingBatches(true);
    setBatchesError(null);
    try {
      const response = await GetTalliesBatches();
      const allFormattedData = response.map(batch => ({
        id: batch.tallyUploadID || batch.batchNumber,
        clientId: batch.clientCode,
        clientName: batch.clientName || 'Unknown Client',
        date: formatDateForDisplay(batch.batchDate),
        dayOfWeek: getDayOfWeek(batch.batchDate),
        batchNumber: batch.batchNumber || 'N/A',
        nonDonors: batch.nonDonors || 0,
        donations: batch.donations || 0,
        amount: batch.amount || 0,
        isPending: batch.isPending,
        statusText: batch.statusText,
        rowCount: batch.rowCount,
        pendingRowCount: batch.pendingRowCount,
        tallyUploadID: batch.tallyUploadID,
        originalData: batch
      }));

      if (status === 'pending') {
        setPendingBatchData(allFormattedData.filter(b => b.isPending === true));
      } else {
        setSuccessfulBatchData(allFormattedData.filter(b => b.isPending === false));
      }

      return allFormattedData;
    } catch (error) {
      console.error("Error fetching batches:", error);
      setBatchesError("Failed to load batches. Please try again.");
      return [];
    } finally {
      setLoadingBatches(false);
    }
  };


  const refreshAllBatches = async () => {
    await Promise.all([
      fetchBatches('pending'),
      fetchBatches('successful')
    ]);
  };


  const fetchBatchDetails = async (tallyUploadID) => {
    try {
      setLoadingListSummary(true);

      const response = await GetBatchDetails(tallyUploadID);

      if (response && response.success && response.data && response.data.details) {
        const transformedData = response.data.details.map((item, index) => ({
          id: item.tallyID || `row-${index}-${Date.now()}`,
          mailCode: item.mailCode || '',
          nonDonors: item.nonDonors || 0,
          donations: item.donors || 0,
          amount: item.amount || 0,
          status: item.status || 'Unknown',
          clientName: item.clientName,
          clientCode: item.clientCode,
          jobName: item.jobName,
          batchDate: item.batchDate,
          isPending: item.isPending,
          tallyID: item.tallyID,
          originalData: item
        }));

        setMailCodeData(transformedData);

        if (response.data.totals) {
          setBatchTotals(response.data.totals);
        }

        setSelectedMailCodeRows([]);
        setIsManageMailCodesActive(false);
        setHasNewRow(false);
        setSelectAllChecked(false);
        setIsIndeterminate(false);
      } else {
        setMailCodeData([]);
        setBatchTotals(null);

        if (response && response.message) {
          showError(response.message);
        }
      }

    } catch (error) {
      console.error('Error fetching batch details:', error);
      showError(`Failed to load batch details. Please try again.`);
      setMailCodeData([]);
      setBatchTotals(null);
    } finally {
      setLoadingListSummary(false);
    }
  };


  const handleAddNewRow = useCallback(() => {
    if (!gridApi || !isEditAllowed) {
      showError('Cannot add new rows to successful batch. Only pending batches can be modified.');
      return;
    }
    gridApi.stopEditing();

    const newRow = {
      id: `temp-${Date.now()}`,
      mailCode: '',
      nonDonors: 0,
      donations: 0,
      amount: 0,
      status: 'Pending',
      isNewRow: true
    };

    setMailCodeData(prev => {
      const filtered = prev.filter(row => !row.isNewRow);
      return [...filtered, newRow];
    });
    setHasNewRow(true);

    setTimeout(() => {
      if (!gridApi) return;
      gridApi.forEachNode(node => {
        if (node.data?.isNewRow) {
          gridApi.ensureIndexVisible(node.rowIndex);
          gridApi.startEditingCell({
            rowIndex: node.rowIndex,
            colKey: 'mailCode'
          });
        }
      });
    }, 100);
  }, [gridApi, isEditAllowed]);


  const handleGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);


  const handleCellClicked = useCallback((params) => {
    if (params?.data?.isAddRow && isManageMailCodesActive && isEditAllowed) {
      handleAddNewRow();
    }
  }, [handleAddNewRow, isManageMailCodesActive, isEditAllowed]);


  const handleCellValueChanged = useCallback((params) => {
    if (params.data && !params.data?.isTotalsRow) {
      setMailCodeData(prev =>
        prev.map(row =>
          row.id === params.data.id ? { ...params.data } : row
        )
      );
    }
  }, []);


  const handleCellEditingStopped = useCallback((params) => {
    if (params.api && params.node && !params.data?.isTotalsRow) {
      params.api.refreshCells({
        rowNodes: [params.node],
        force: true
      });
    }
  }, []);

  // Function to handle Row Value Changed
  const handleRowValueChanged = useCallback((params) => {
    if (params.data.id && params.data.id.toString().startsWith('temp-')) {
      setMailCodeData(prev =>
        prev.map(row =>
          row.id === params.data.id ? { ...params.data } : row
        )
      );
    }
  }, []);


  const handleSelectAll = useCallback(() => {
    if (!gridApi) return;

    const newSelectAllState = !selectAllChecked;
    setSelectAllChecked(newSelectAllState);
    setIsIndeterminate(false);

    if (newSelectAllState) {
      const selectedRows = [];
      gridApi.forEachNode(node => {
        if (!node.data?.isAddRow && !node.data?.isNewRow && !node.data?.isTotalsRow) {
          node.setSelected(true);
          selectedRows.push(node.data);
        }
      });
      setSelectedMailCodeRows(selectedRows);
    } else {
      gridApi.deselectAll();
      setSelectedMailCodeRows([]);
    }
  }, [gridApi, selectAllChecked]);


  const handleRowCheckboxChange = useCallback((rowData, checked) => {
    if (checked) {
      setSelectedMailCodeRows(prev => [...prev, rowData]);
    } else {
      setSelectedMailCodeRows(prev => prev.filter(row => row.id !== rowData.id));
    }
  }, []);


  const calculateTotals = useMemo(() => {
    if (!mailCodeData || mailCodeData.length === 0) {
      return {
        nonDonors: 0,
        donations: 0,
        amount: 0,
        rowCount: 0
      };
    }

    const validRows = mailCodeData.filter(row =>
      !row.isAddRow && !row.isNewRow && row.mailCode && row.mailCode.trim() !== ''
    );

    return {
      nonDonors: validRows.reduce((sum, row) => sum + (Number(row.nonDonors) || 0), 0),
      donations: validRows.reduce((sum, row) => sum + (Number(row.donations) || 0), 0),
      amount: validRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
      rowCount: validRows.length
    };
  }, [mailCodeData]);


  const totalsRow = useMemo(() => {
    if (!hasSelectedBatch || mailCodeData.length === 0) {
      return null;
    }

    const validRows = mailCodeData.filter(row =>
      !row.isAddRow && !row.isNewRow && row.mailCode && row.mailCode.trim() !== ''
    );

    if (validRows.length === 0) {
      return null;
    }

    return {
      id: 'totals-row',
      mailCode: 'TOTAL',
      nonDonors: validRows.reduce((sum, row) => sum + (Number(row.nonDonors) || 0), 0),
      donations: validRows.reduce((sum, row) => sum + (Number(row.donations) || 0), 0),
      amount: validRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
      status: '',
      isTotalsRow: true,
      isAddRow: false,
      isNewRow: false
    };
  }, [mailCodeData, hasSelectedBatch]);

  // Final row data with Add Row placeholder
  const rowDataWithAddRow = useMemo(() => {
    if (!hasSelectedBatch) return [];

    const rows = [...mailCodeData];

    if (isManageMailCodesActive && !hasNewRow && isEditAllowed) {
      rows.push({
        id: 'add-row-placeholder',
        mailCode: '',
        nonDonors: '',
        donations: '',
        amount: '',
        status: '',
        isAddRow: true
      });
    }

    return rows;
  }, [mailCodeData, isManageMailCodesActive, hasNewRow, hasSelectedBatch, isEditAllowed]);


  const pinnedBottomRowData = useMemo(() => {
    if (!hasSelectedBatch || !totalsRow) return [];
    return [totalsRow];
  }, [hasSelectedBatch, totalsRow]);


  const handleDelete = async (batchData, tallyIds = null) => {
    if (!batchData || !batchData.tallyUploadID) {
      showError('Invalid batch data: missing tallyUploadID. Cannot perform delete operation.');
      return;
    }

    let confirmMessage;
    if (tallyIds && tallyIds.length > 0) {
      confirmMessage = `Are you sure you want to delete ${tallyIds.length} selected row(s)? This action cannot be undone.`;
    } else {
      confirmMessage = `Are you sure you want to delete batch ${batchData.batchNumber}? This action cannot be undone.`;
    }

    setDeleteContext({
      batchData,
      tallyIds
    });
    setShowDeleteConfirm(true);
    return;


    setIsDeleting(true);

    try {
      const requestBody = {};

      if (tallyIds && tallyIds.length > 0) {
        const validTallyIds = tallyIds.filter(id =>
          id && typeof id === 'number' && id > 0
        );

        if (validTallyIds.length > 0) {
          requestBody.tallyIds = validTallyIds;
        }
      }

      requestBody.tallyUploadId = batchData.tallyUploadID;

      await DeleteTalliesRows(batchData.batchNumber, requestBody);

      if (tallyIds && tallyIds.length > 0) {
        const updatedMailCodeData = mailCodeData.filter(
          row => !selectedMailCodeRows.some(selectedRow => selectedRow.id === row.id)
        );

        setMailCodeData(updatedMailCodeData);
        setSelectedMailCodeRows([]);
        setSelectAllChecked(false);
        setIsIndeterminate(false);

        showSuccess(`${tallyIds.length} row(s) deleted successfully`);

        if (updatedMailCodeData.length === 0) {
          setSelectedBatchDetails(null);
          setHasSelectedBatch(false);
          setViewBatchDate('');
          setViewBatchNumber('');
          setMailCodeData([]);
          setBatchTotals(null);
          setIsManageMailCodesActive(false);
          setSelectedMailCodeRows([]);
          setHasNewRow(false);
          setSelectAllChecked(false);
          setIsIndeterminate(false);

          await refreshAllBatches();
        }
      } else {
        if (selectedBatchDetails && selectedBatchDetails.tallyUploadID === batchData.tallyUploadID) {
          setSelectedBatchDetails(null);
          setHasSelectedBatch(false);
          setViewBatchDate('');
          setViewBatchNumber('');
          setMailCodeData([]);
          setBatchTotals(null);
          setIsManageMailCodesActive(false);
          setSelectedMailCodeRows([]);
          setHasNewRow(false);
          setSelectAllChecked(false);
          setIsIndeterminate(false);
        }

        showSuccess(`Batch ${batchData.batchNumber} deleted successfully`);
        await refreshAllBatches();
      }

    } catch (error) {
      console.error('Error during delete operation:', error);
      showError('Failed to delete. Please try again. Error: ' + (error.message || 'Unknown error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteContext) return;

    const { batchData, tallyIds } = deleteContext;

    setShowDeleteConfirm(false);
    setIsDeleting(true);

    try {
      const requestBody = {};

      if (tallyIds && tallyIds.length > 0) {
        const validTallyIds = tallyIds.filter(
          id => id && typeof id === 'number' && id > 0
        );

        if (validTallyIds.length > 0) {
          requestBody.tallyIds = validTallyIds;
        }
      }

      requestBody.tallyUploadId = batchData.tallyUploadID;

      await DeleteTalliesRows(batchData.batchNumber, requestBody);


      if (tallyIds && tallyIds.length > 0) {
        const updatedMailCodeData = mailCodeData.filter(
          row => !selectedMailCodeRows.some(selectedRow => selectedRow.id === row.id)
        );

        setMailCodeData(updatedMailCodeData);
        setSelectedMailCodeRows([]);
        setSelectAllChecked(false);
        setIsIndeterminate(false);

        showSuccess(`${tallyIds.length} row(s) deleted successfully`);

        if (updatedMailCodeData.length === 0) {
          handleCloseBatchDetails();
          await refreshAllBatches();
        }
      }

      else {
        if (selectedBatchDetails?.tallyUploadID === batchData.tallyUploadID) {
          handleCloseBatchDetails();
        }

        showSuccess(`Batch ${batchData.batchNumber} deleted successfully`);
        await refreshAllBatches();
      }

    } catch (error) {
      console.error('Delete error:', error);
      showError('Failed to delete. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteContext(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteContext(null);
  };


  const handleDeleteBatch = (batchData) => {
    handleDelete(batchData, null);
  };


  const handleDeleteSpecificRows = () => {
    if (!selectedBatchDetails || !selectedBatchDetails.tallyUploadID) {
      showError('No batch selected or invalid batch data');
      return;
    }

    if (!selectedMailCodeRows || selectedMailCodeRows.length === 0) {
      showError('Please select at least one row to delete');
      return;
    }

    const tallyIds = selectedMailCodeRows
      .map(row => row.tallyID)
      .filter(id => id && typeof id === 'number' && id > 0);

    if (tallyIds.length === 0) {
      showError('Selected rows do not have valid tally IDs for deletion');
      return;
    }

    handleDelete(selectedBatchDetails, tallyIds);
  };


  const handleSaveChangesClick = async () => {
    if (!isEditAllowed) {
      showError('Cannot save changes to successful batch. Only pending batches can be modified.');
      return;
    }

    if (gridApi) {
      gridApi.stopEditing();
    }

    if (!selectedBatchDetails || !selectedBatchDetails.tallyUploadID) {
      showError('No batch selected or invalid batch data');
      return;
    }

    if (!validateBatchDate(viewBatchDate)) {
      return;
    }

    const allRows = [];
    gridApi.forEachNode((node) => {
      if (!node.rowPinned && !node.data?.isAddRow && !node.data?.isTotalsRow) {
        allRows.push(node.data);
      }
    });

    const newRows = allRows.filter(row =>
      row.id && row.id.toString().startsWith('temp-')
    );

    const existingRows = allRows.filter(row =>
      row.id && !row.id.toString().startsWith('temp-') && row.tallyID
    );

    const validNewRows = newRows.filter(row =>
      row.mailCode && row.mailCode.trim() !== ''
    );

    const updatedRows = [];

    existingRows.forEach(row => {
      if (row.mailCode && row.mailCode.trim() !== '') {
        updatedRows.push({
          TallyID: Number(row.tallyID) || 0,
          Mailcode: row.mailCode,
          Donations: Number(row.donations) || 0,
          Nondonors: Number(row.nonDonors) || 0,
          Amount: Number(row.amount) || 0
        });
      }
    });

    validNewRows.forEach(row => {
      updatedRows.push({
        Mailcode: row.mailCode,
        Donations: Number(row.donations) || 0,
        Nondonors: Number(row.nonDonors) || 0,
        Amount: Number(row.amount) || 0
      });
    });

    if (updatedRows.length === 0) {
      showError('No valid data to save');
      return;
    }

    const saveData = {
      batchDate: viewBatchDate,
      tallyUploadID: selectedBatchDetails.tallyUploadID,
      updatedRows: updatedRows
    };

    const confirmSave = window.confirm(`Do you want to Save changes`);
    if (!confirmSave) return;

    setIsDeleting(true);

    try {
      const response = await SaveBatchData(selectedBatchDetails.batchNumber, saveData);

      showSuccess(`Successfully saved ${updatedRows.length} row(s)!`);

      // Refresh both batch grids so the batch lands in the right list
      const allBatches = await fetchBatches('successful');
      await fetchBatches('pending');

      // Check if the batch is now fully successful (no errors)
      const updatedBatch = allBatches.find(
        b => b.tallyUploadID === selectedBatchDetails.tallyUploadID
      );

      if (updatedBatch && updatedBatch.isPending === false) {

        handleCloseBatchDetails();
        setTimeout(() => setActiveBatchTab('successful'), 0);
      } else {

        if (selectedBatchDetails.tallyUploadID) {
          await fetchBatchDetails(selectedBatchDetails.tallyUploadID);
        }
        setIsManageMailCodesActive(false);
        setHasNewRow(false);
        setSelectedMailCodeRows([]);
        setSelectAllChecked(false);
        setIsIndeterminate(false);
      }

    } catch (error) {
      console.error('Error saving changes:', error);

      let errorMessage = 'Failed to save changes. ';
      if (error.response) {
        if (error.response.status === 400) {
          errorMessage += error.response.data?.message || 'Invalid data.';
        } else if (error.response.status === 404) {
          errorMessage += 'Batch not found.';
        } else {
          errorMessage += 'Server error.';
        }
      } else if (error.request) {
        errorMessage += 'No response from server.';
      } else {
        errorMessage += error.message;
      }

      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const clientOptions = useMemo(() => {
    return [...clients]
      .sort((a, b) => a.clientName.localeCompare(b.clientName))
      .map(client => ({
        value: client.clientCode,
        label: client.clientName,
        clientId: client.clientID,
        clientCode: client.clientCode
      }));
  }, [clients]);

  // Filtered pending batch data with client and search filters
  const filteredPendingBatchData = useMemo(() => {
    let data = pendingBatchData;

    if (selectedPendingClient) {
      data = data.filter(batch => batch.clientId === selectedPendingClient);
    }

    if (searchPendingBatchTerm) {
      const searchTermLower = searchPendingBatchTerm.toLowerCase();
      data = data.filter(batch =>
        batch.batchNumber.toString().toLowerCase().includes(searchTermLower) ||
        batch.date.includes(searchTermLower)
      );
    }

    return data;
  }, [pendingBatchData, searchPendingBatchTerm, selectedPendingClient]);

  const filteredSuccessfulBatchData = useMemo(() => {
    let data = successfulBatchData;

    if (selectedClient) {
      data = data.filter(batch => batch.clientId === selectedClient);
    }

    if (mailCodeFilterContext) {
      data = data.filter(batch =>
        mailCodeFilterContext.batchNumbers.has(batch.batchNumber.toString().toLowerCase().trim())
      );
    } else if (searchSuccessfulBatchTerm) {
      const searchTermLower = searchSuccessfulBatchTerm.toLowerCase();
      data = data.filter(batch =>
        batch.batchNumber.toString().toLowerCase().includes(searchTermLower) ||
        batch.date.includes(searchTermLower)
      );
    }

    return data;
  }, [successfulBatchData, selectedClient, searchSuccessfulBatchTerm, mailCodeFilterContext]);

  // Handle View Batch
  const handleViewBatch = async (batchData) => {

    setSelectedBatchDetails(batchData);
    setHasSelectedBatch(true);

    let formattedDate = '';
    if (batchData.originalData && batchData.originalData.batchDate) {
      formattedDate = formatDateForInput(batchData.originalData.batchDate);
    }

    setViewBatchDate(formattedDate);
    setViewBatchNumber(batchData.batchNumber);

    setUploadBatchDate('');
    setUploadBatchNumber('');

    setSelectedMailCodeRows([]);
    setIsManageMailCodesActive(false);
    setHasNewRow(false);
    setSelectAllChecked(false);
    setIsIndeterminate(false);

    if (batchData.tallyUploadID) {
      await fetchBatchDetails(batchData.tallyUploadID);
    } else {
      showError('Invalid batch data: missing tallyUploadID');
    }
  };

  // Batch Column Definitions
  const batchColumnDefs = useMemo(() => {
    const columns = [
      {
        headerName: 'View',
        field: 'view',
        width: 120,
        maxWidth: 200,
        minWidth: 100,
        cellRenderer: (params) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              height: '100%'
            }}
            onClick={() => handleViewBatch(params.data)}
          >
            <Eye size={18} color="#2E6F9F" />
          </div>
        ),
        sortable: false,
        filter: false,
      },
      {
        headerName: 'Client Code',
        field: 'clientId',
        minWidth: 180,
        flex: 1.5,
        filter: 'agTextColumnFilter',
        cellRenderer: (params) => {
          const value = params.value || '';
          const displayValue = value.length > 12 ? value.substring(0, 12) + '...' : value;

          return React.createElement('div', {
            style: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            },
            title: value
          }, displayValue);
        }
      },
      {
        headerName: 'Date',
        field: 'date',
        minWidth: 180,
        comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
        flex: 1.5,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => {
          if (!params.value) return '';
          return params.value;
        }
      },
      {
        headerName: 'Day of Week',
        field: 'dayOfWeek',
        minWidth: 180,
        flex: 1.5,
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Batch Number',
        field: 'batchNumber',
        minWidth: 220,
        flex: 1.5,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => params.value == null ? '' : params.value,
      },
      {
        headerName: 'Non Donors',
        field: 'nonDonors',
        minWidth: 180,
        flex: 1.5,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return '0';
          const numValue = Number(params.value);
          if (isNaN(numValue)) return '0';
          return numValue.toLocaleString('en-US');
        },
      },
      {
        headerName: 'Donations',
        field: 'donations',
        minWidth: 180,
        flex: 1.5,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return '0';
          const numValue = Number(params.value);
          if (isNaN(numValue)) return '0';
          return numValue.toLocaleString('en-US');
        },
      },
      {
        headerName: ' Amount',
        field: 'amount',
        minWidth: 180,
        flex: 1.5,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => {
          const numValue = Number(params.value);
          if (isNaN(numValue) || numValue === 0) return '$0.00';
          return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
      }
    ];
    if (canDeleteTallies) {
      columns.push({
        headerName: 'Delete',
        field: 'delete',
        minWidth: 180,
        flex: 1.5,
        cellRenderer: (params) => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                height: '100%',
                opacity: isDeleting ? 0.5 : 1,
                pointerEvents: isDeleting ? 'none' : 'auto'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteBatch(params.data);
              }}
            >
              <Trash2 size={18} color="#dc2626" />
            </div>
          );
        },
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
      });
    }

    return columns;
  }, [isDeleting, canDeleteTallies]);


  const mailCodeColumnDefs = useMemo(() => {
    const columns = [];


    if (isManageMailCodesActive) {
      columns.push({
        headerName: '',
        field: 'selection',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        headerComponent: () => {
          return (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              <input
                type="checkbox"
                ref={input => {
                  if (input) {
                    input.indeterminate = isIndeterminate;
                  }
                }}
                checked={selectAllChecked}
                onChange={handleSelectAll}
                style={{ cursor: 'pointer', margin: 0 }}
                title={selectAllChecked ? "Deselect all" : "Select all"}
              />
            </div>
          );
        },
        cellRenderer: (params) => {

          if (params.node.rowPinned) return null;
          if (params.data?.isAddRow) return null;
          return (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              <input
                type="checkbox"
                checked={selectedMailCodeRows.some(row => row.id === params.data.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleRowCheckboxChange(params.data, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: 'pointer', margin: 0 }}
              />
            </div>
          );
        },
        sortable: false,
        filter: false,
        editable: false
      });
    }

    // Add the rest of the columns
    columns.push(
      {
        headerName: 'Mail Code',
        field: 'mailCode',
        width: 120,
        maxWidth: 200,
        minWidth: 100,
        suppressSizeToFit: true,
        sortable: true,
        filter: 'agTextColumnFilter',
        editable: (params) => {
          if (params.node.rowPinned) return false;
          if (params.data?.isTotalsRow) return false;
          return !params.data?.isAddRow && isManageMailCodesActive && isEditAllowed;
        },
        cellRenderer: (params) => {
          if (isTotalsRow(params)) {
            return (
              <strong style={boldStyle}>
                TOTAL
              </strong>
            );
          }
          if (params.data?.isAddRow) {
            return isManageMailCodesActive && isEditAllowed ? '+ Add New Record' : '';
          }

          // Check if this row should show error icon
          let showErrorIcon = false;
          if (params.data?.status) {
            const cleanValue = params.data.status.replace(/\?+$/, '').trim();
            if (cleanValue !== 'Success' && cleanValue !== 'Pending') {
              showErrorIcon = true;
            }
          }

          if (showErrorIcon) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon name="error-icon" size={16} />
                <span>{params.value || ''}</span>
              </div>
            );
          }
          return params.value || '';
        },
        cellStyle: (params) => {
          if (isTotalsRow(params)) {
            return {
              backgroundColor: '#f0f8ff',
              paddingLeft: '15px',
              borderTop: '0.5px solid #babfc7'
            };
          }
          if (params.data?.isAddRow) {
            return {
              textAlign: 'left',
              fontWeight: 'bold',
              color: isEditAllowed ? '#2889a7' : '#999',
              cursor: isManageMailCodesActive && isEditAllowed ? 'pointer' : 'default',
              backgroundColor: '#f9fafb'
            };
          }
          if (params.data?.isNewRow) {
            return {
              backgroundColor: '#fff9e6',
            };
          }
          return null;
        }
      },
      {
        headerName: 'Non Donors',
        field: 'nonDonors',
        minWidth: 100,
        flex: 1,
        sortable: true,
        filter: 'agTextColumnFilter',
        editable: (params) => {
          if (params.node.rowPinned) return false;
          if (params.data?.isTotalsRow) return false;
          return !params.data?.isAddRow && isManageMailCodesActive && isEditAllowed;
        },
        cellRenderer: (params) => {
          if (isTotalsRow(params)) {
            const value = Number(params.value) || 0;
            return (
              <strong style={{ ...boldStyle, textAlign: 'right' }}>
                {value.toLocaleString('en-US')}
              </strong>
            );
          }
          if (params.value === null || params.value === undefined || params.value === '') {
            return '';
          }
          const numValue = Number(params.value);
          if (isNaN(numValue)) return '';
          return numValue.toLocaleString('en-US');
        },
        valueParser: (params) => {
          if (!params.newValue) return 0;
          const cleanValue = String(params.newValue).replace(/,/g, '');
          return Number(cleanValue) || 0;
        },
        cellStyle: (params) => {
          if (isTotalsRow(params)) {
            return {
              backgroundColor: '#f0f8ff',
              textAlign: 'right',
              paddingRight: '15px',
              borderTop: '0.5px solid #babfc7'
            };
          }
          if (params.data?.isAddRow) {
            return {
              backgroundColor: '#f9fafb'
            };
          }
          if (params.data?.isNewRow) {
            return {
              backgroundColor: '#fff9e6',
            };
          }
          return { textAlign: 'right' };
        }
      },
      {
        headerName: 'Donations',
        field: 'donations',
        minWidth: 100,
        flex: 1,
        sortable: true,
        filter: 'agTextColumnFilter',
        editable: (params) => {
          if (params.node.rowPinned) return false;
          if (params.data?.isTotalsRow) return false;
          return !params.data?.isAddRow && isManageMailCodesActive && isEditAllowed;
        },
        cellRenderer: (params) => {
          if (isTotalsRow(params)) {
            const value = Number(params.value) || 0;
            return (
              <strong style={{ ...boldStyle, textAlign: 'right' }}>
                {value.toLocaleString('en-US')}
              </strong>
            );
          }
          if (params.value === null || params.value === undefined || params.value === '') {
            return '';
          }
          const numValue = Number(params.value);
          if (isNaN(numValue)) return '';
          return numValue.toLocaleString('en-US');
        },
        valueParser: (params) => {
          if (!params.newValue) return 0;
          const cleanValue = String(params.newValue).replace(/,/g, '');
          return Number(cleanValue) || 0;
        },
        cellStyle: (params) => {
          if (isTotalsRow(params)) {
            return {
              backgroundColor: '#f0f8ff',
              textAlign: 'right',
              paddingRight: '15px',
              borderTop: '0.5px solid #babfc7'
            };
          }
          if (params.data?.isAddRow) {
            return {
              backgroundColor: '#f9fafb'
            };
          }
          if (params.data?.isNewRow) {
            return {
              backgroundColor: '#fff9e6',
            };
          }
          return { textAlign: 'right' };
        }
      },
      {
        headerName: 'Amount',
        field: 'amount',
        minWidth: 150,
        maxWidth: 150,
        flex: 1,
        sortable: true,
        filter: 'agTextColumnFilter',
        editable: (params) => {
          if (params.node.rowPinned) return false;
          if (params.data?.isTotalsRow) return false;
          return !params.data?.isAddRow && isManageMailCodesActive && isEditAllowed;
        },
        // ✅ cellRenderer with <strong> tag for guaranteed bold value
        cellRenderer: (params) => {
          if (isTotalsRow(params)) {
            const value = Number(params.value) || 0;
            return (
              <strong style={{ ...boldStyle, textAlign: 'right' }}>
                {`$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </strong>
            );
          }
          if (params.value === null || params.value === undefined || params.value === '') {
            return '-';
          }
          if (params.data?.isAddRow) {
            return '';
          }
          const numValue = Number(params.value);
          if (isNaN(numValue)) return '-';
          return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        valueParser: (params) => {
          if (!params.newValue) return 0;
          const cleanValue = String(params.newValue).replace(/[$,\s]/g, '');
          return Number(cleanValue) || 0;
        },
        cellStyle: (params) => {
          if (isTotalsRow(params)) {
            return {
              backgroundColor: '#f0f8ff',
              textAlign: 'right',
              paddingRight: '15px',
              borderTop: '0.5px solid #babfc7'
            };
          }
          if (params.data?.isAddRow) {
            return {
              backgroundColor: '#f9fafb'
            };
          }
          if (params.data?.isNewRow) {
            return {
              backgroundColor: '#fff9e6',
            };
          }
          return { textAlign: 'right' };
        }
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        sortable: true,
        filter: 'agTextColumnFilter',
        editable: false,
        cellRenderer: (params) => {
          if (isTotalsRow(params)) {
            return <strong style={boldStyle}></strong>;
          }
          if (params.data?.isAddRow) {
            return '';
          }

          // Clean the value - remove any trailing ? and trim
          const cleanValue = params.value ? params.value.replace(/\?+$/, '').trim() : '';

          // Set color: black for Success, red for everything else
          let color = '#DA1E28';

          if (cleanValue === 'Success') {
            color = '#000000';
          }

          const displayText = cleanValue || '';

          return (
            <span
              style={{
                color: color,
                fontWeight: 500,
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
            >
              {displayText}
            </span>
          );
        },
        cellStyle: (params) => {
          if (isTotalsRow(params)) {
            return {
              backgroundColor: '#f0f8ff',
              borderTop: '0.5px solid #babfc7'
            };
          }
          return null;
        }
      }
    );

    return columns;
  }, [isManageMailCodesActive, selectAllChecked, isIndeterminate, handleSelectAll, selectedMailCodeRows, handleRowCheckboxChange, isEditAllowed]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const validateAndSetFile = (file) => {
    if (!validateFile(file)) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        const validation = validateExcelFormat(jsonData);

        if (validation.isValid) {
          setUploadedFile(file);
          setFileError('');
        } else {
          setFileError(validation.message);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      } catch (error) {
        console.error('Error reading file:', error);
        setFileError('Error reading file. Please check the file format.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadResponse(null);
    setUploadError(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload form handlers
  const handleUploadBatchDateChange = (e) => {
    const date = e.target.value;
    setUploadBatchDate(date);
    validateBatchDate(date);
  };

  const handleUploadBatchNumberChange = (e) => {
    const number = e.target.value;
    setUploadBatchNumber(number);
    validateBatchNumber(number);
  };

  // View form handlers
  const handleViewBatchDateChange = (e) => {
    if (!isEditAllowed) return;
    const date = e.target.value;
    setViewBatchDate(date);
    validateBatchDate(date);
  };

  const handleViewBatchNumberChange = (e) => {
    if (!isEditAllowed) return;
    const number = e.target.value;
    setViewBatchNumber(number);
    validateBatchNumber(number);
  };

  // Updated handleUploadFileToAPI with dynamic success/error messages
  const handleUploadFileToAPI = async () => {
    const isBatchDateValid = validateBatchDate(uploadBatchDate);

    if (!uploadBatchNumber || uploadBatchNumber.trim() === '') {
      setBatchNumberError('Batch Number is required');
      return;
    }

    const isBatchNumberValid = validateBatchNumber(uploadBatchNumber);

    if (!isBatchDateValid || !isBatchNumberValid) {
      return;
    }

    if (!uploadedFile) {
      setFileError('Please select a file');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadResponse(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          const validation = validateExcelFormat(jsonData);
          if (!validation.isValid) {
            throw new Error(validation.message);
          }

          const talliesRows = jsonData.map((row, index) => ({
            mailCode: row['Mailcode'] || '',
            donations: Number(row['Donations']) || 0,
            nonDonors: Number(row['Nondonors']) || 0,
            amount: Number(row['Amount']) || 0
          }));

          const listData = {
            returnsType: returns,
            batchDate: uploadBatchDate,
            batchNumber: uploadBatchNumber
          };

          const response = await UploadTalliesIntegration(listData, talliesRows);

          setUploadResponse(response);

          if (response.errorRows && response.errorRows > 0) {
            const errorMsg = `Your file was uploaded, but there were ${response.errorRows} error(s) found. Please resolve these in order to create a successful batch.`;
            showError(errorMsg);
          }
          else if (response.isPending === false && response.errorRows === 0 && response.successRows > 0) {
            showSuccess('Your file has been uploaded and there were no errors found.');
          }
          else if (response.message === 'Batch processed successfully') {
            showSuccess('Your file has been uploaded and there were no errors found.');
          }
          else if (response.message && response.message.includes('pending review')) {
            const errorMsg = `Your file was uploaded, but there were ${response.errorRows || 0} error(s) found. Please resolve these in order to create a successful batch.`;
            showError(errorMsg);
          }
          else {
            showSuccess('Your file has been uploaded successfully!');
          }

          if (response.batchNumber) {
            setUploadBatchNumber(response.batchNumber);
          }

          fetchBatches(activeBatchTab);

          handleRemoveFile();
          setUploadBatchDate('');
          setUploadBatchNumber('');
          setReturns('Non-Cumulative');
          setBatchDateError('');
          setBatchNumberError('');

        } catch (parseError) {
          console.error('Error parsing Excel:', parseError);
          setUploadError(parseError.message || 'Failed to parse Excel file. Please check the format.');
          showError(`Upload failed: ${parseError.message || 'Failed to parse Excel file. Please check the format.'}`);
        }
        setIsUploading(false);
      };

      reader.onerror = () => {
        setUploadError('Failed to read file');
        showError('Failed to read file');
        setIsUploading(false);
      };

      reader.readAsArrayBuffer(uploadedFile);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(error.message || 'Failed to upload file');
      showError('Upload failed: ' + (error.message || 'Please try again'));
      setIsUploading(false);
    }
  };

  // Handle Save button click to call UpdateBatchDetails API
  const handleSave = async () => {
    if (!isEditAllowed) {
      showError('Cannot save changes to successful batch. Only pending batches can be modified.');
      return;
    }

    const isBatchDateValid = validateBatchDate(viewBatchDate);
    const isBatchNumberValid = validateBatchNumber(viewBatchNumber);

    if (!isBatchDateValid || !isBatchNumberValid) {
      return;
    }

    if (!hasSelectedBatch || !selectedBatchDetails) {
      showError('No batch selected');
      return;
    }

    if (!selectedBatchDetails.tallyUploadID) {
      showError('Invalid batch data: missing tallyUploadID');
      return;
    }

    setIsDeleting(true);

    try {
      const [year, month, day] = viewBatchDate.split('-').map(Number);
      const apiDate = new Date(year, month - 1, day);

      const response = await UpdateBatchDetails({
        tallyUploadId: selectedBatchDetails.tallyUploadID,
        newBatchNumber: viewBatchNumber,
        newBatchDate: apiDate.toISOString()
      });

      if (response?.errors) {
        const firstErrorField = Object.keys(response?.errors)[0];
        if (firstErrorField) {
          const errorValue = response?.errors[firstErrorField];
          const errorMessage = Array.isArray(errorValue) ? errorValue[0] : errorValue;

          if (errorMessage.includes('New Batch Date cannot be less then the MailDate') ||
            errorMessage.includes('cannot be less than')) {
            showError('Batch date cannot be earlier than the mail date. Please select a valid date.');
          } else {
            alert(`${errorMessage}`);
          }
          return;
        }
      }

      if (response?.data?.message && response?.success === false) {
        if (response.data.message.includes('New Batch Date cannot be less then the MailDate')) {
          showError('Batch date cannot be earlier than the mail date. Please select a valid date.');
          return;
        }
        showError(response.data.message);
        return;
      }

      if (response?.message && response?.success === false) {
        if (response.message.includes('New Batch Date cannot be less then the MailDate')) {
          showError('Batch date cannot be earlier than the mail date. Please select a valid date.');
          return;
        }
        showError(response.message);
        return;
      }

      showSuccess('Batch details updated successfully!');


      const allBatches = await fetchBatches('successful');
      await fetchBatches('pending');


      const updatedBatch = allBatches.find(
        b => b.tallyUploadID === selectedBatchDetails.tallyUploadID
      );

      if (updatedBatch && updatedBatch.isPending === false) {

        handleCloseBatchDetails();
        setTimeout(() => setActiveBatchTab('successful'), 0);
      } else {

        setSelectedBatchDetails({
          ...selectedBatchDetails,
          batchNumber: viewBatchNumber,
          originalData: {
            ...selectedBatchDetails.originalData,
            batchDate: apiDate.toISOString()
          }
        });
      }

    } catch (error) {
      console.error('Error updating batch details:', error);

      let errorMessage = 'Failed to update batch details. ';

      if (error.response) {
        if (error.response.status === 400) {
          const data = error.response.data;

          if (data?.message && data.message.includes('New Batch Date cannot be less then the MailDate')) {
            errorMessage = 'Batch date cannot be less than the mail date. Please select a valid date.';
          } else if (data?.data?.message && data.data.message.includes('New Batch Date cannot be less then the MailDate')) {
            errorMessage = 'Batch date cannot be less than the mail date. Please select a valid date.';
          } else {
            errorMessage += data?.message || 'Invalid data provided.';
          }
        } else if (error.response.status === 404) {
          errorMessage += 'Batch not found.';
        } else if (error.response.status === 409) {
          errorMessage += 'Batch number already exists.';
        } else {
          errorMessage += 'Server error.';
        }
      } else if (error.request) {
        errorMessage += 'No response from server.';
      } else {
        errorMessage += error.message;
      }

      showError(errorMessage);

    } finally {
      setIsDeleting(false);
    }
  };

  const isUploadEnabled = useMemo(() => {
    if (!uploadBatchDate || batchDateError) {
      return false;
    }

    if (!uploadBatchNumber || batchNumberError) {
      return false;
    }

    return uploadedFile && !fileError;
  }, [uploadBatchDate, batchDateError, uploadBatchNumber, batchNumberError, uploadedFile, fileError]);

  // Handle Manage Mail Codes Click
  const handleManageMailCodesClick = () => {
    if (!hasSelectedBatch) {
      showError('Please select a batch first');
      return;
    }
    setIsManageMailCodesActive(true);
    setHasNewRow(false);
    setSelectedMailCodeRows([]);
    setSelectAllChecked(false);
    setIsIndeterminate(false);
  };

  const handleDeleteClick = () => {
    handleDeleteSpecificRows();
  };

  // Handle Cancel Click
  const handleCancelClick = () => {
    setIsManageMailCodesActive(false);
    setSelectedMailCodeRows([]);
    setHasNewRow(false);
    setSelectAllChecked(false);
    setIsIndeterminate(false);

    setMailCodeData(prev => prev.filter(row => !row.isNewRow));
  };

  // Handle Mail Code Selection Changed
  const handleMailCodeSelectionChanged = (params) => {
    const selectedRows = params.api.getSelectedRows();

  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle Close Batch Details
  const handleCloseBatchDetails = () => {
    setSelectedBatchDetails(null);
    setHasSelectedBatch(false);
    setViewBatchDate('');
    setViewBatchNumber('');
    setUploadBatchDate('');
    setUploadBatchNumber('');
    setSelectedMailCodeRows([]);
    setIsManageMailCodesActive(false);
    setMailCodeData([]);
    setBatchTotals(null);
    setHasNewRow(false);
    setSelectAllChecked(false);
    setIsIndeterminate(false);
  };

  // Clear search functions
  const clearPendingSearch = () => {
    setSearchPendingBatchTerm('');
  };

  const clearSuccessfulSearch = () => {
    setSearchSuccessfulBatchTerm('');
  };

  // Get row ID for AG-Grid
  const getRowId = useMemo(() => {
    return (params) => params.data.id.toString();
  }, []);

  const todayDate = getTodayDateForInput();

  return (

    <>
      <div className="tallies-sidebar-container">

        <SuccessMessage
          message={successMessage}
          onClose={hideSuccess}
          autoClose={true}
          duration={3000}
          type="success"
        />


        <SuccessMessage
          message={errorMessage}
          onClose={hideError}
          autoClose={false}
          type="error"
        />

        {/* Header */}
        <div className="tallies-sidebar-header">
          <h2 className="tallies-header-title">Integrations</h2>


          <div className="job-details-breadcrumb">
            <nav className="breadcrumb-nav">
              <ol className="breadcrumb-list">
                {location.state?.fromJobDetails ? (

                  location.state?.fromReports ? (

                    <>
                      <li className="breadcrumb-item">
                        <a
                          href="/reports"
                          className="breadcrumb-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate('/reports', {
                              state: {
                                restoreReports: true,
                                reportFilters: location.state?.reportFilters || {},
                                selectedClientId: location.state?.selectedClientId || [],
                                reportType: location.state?.reportType || '',
                                rowData: location.state?.rowData || []
                              }
                            });
                          }}
                        >
                          Reports
                        </a>
                      </li>
                      <li className="breadcrumb-separator">/</li>
                      <li className="breadcrumb-item">
                        <a
                          href="#"
                          className="breadcrumb-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/job-details/${location.state.jobDetailsCode}`, {
                              state: {
                                source: 'reports',
                                fromReports: true,
                                reportFilters: location.state?.reportFilters || {},
                                selectedClientId: location.state?.selectedClientId || [],
                                reportType: location.state?.reportType || '',
                                rowData: location.state?.rowData || [],
                                activeTab: location.state.returnActiveTab || 'list-summary'
                              }
                            });
                          }}
                        >
                          {location.state.jobDetailsCode} Job Details
                        </a>
                      </li>
                      <li className="breadcrumb-separator">/</li>
                      <li className="breadcrumb-item">
                        <span className="breadcrumb-active">Integration</span>
                      </li>
                    </>

                  ) : location.state?.source === 'client-dashboard' ? (

                    <>
                      <li className="breadcrumb-item">
                        <Icon
                          name="user-icon"
                          size={14}
                          alt="user-icon"
                          className="breadcrumb-icon"
                        />
                        <a
                          href="/dashboard"
                          className="breadcrumb-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate('/dashboard');
                          }}
                        >
                          Client Select
                        </a>
                      </li>
                      <li className="breadcrumb-separator">/</li>
                      <li className="breadcrumb-item">
                        <a
                          href="/client-dashboard"
                          className="breadcrumb-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate('/client-dashboard', {
                              state: {
                                clientId: location.state?.clientId,
                                source: 'client-dashboard',
                                isAllClients: false
                              }
                            });
                          }}
                        >
                          Client: {getClientCodeById(location.state?.clientId)}
                        </a>
                      </li>
                      <li className="breadcrumb-separator">/</li>
                      <li className="breadcrumb-item">
                        <a
                          href="#"
                          className="breadcrumb-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/job-details/${location.state.jobDetailsCode}`, {
                              state: {
                                source: 'client-dashboard',
                                isAllClients: location.state?.isAllClients,
                                activeTab: location.state.returnActiveTab || 'list-summary'
                              }
                            });
                          }}
                        >
                          {location.state.jobDetailsCode} Job Details
                        </a>
                      </li>
                      <li className="breadcrumb-separator">/</li>
                      <li className="breadcrumb-item">
                        <span className="breadcrumb-active">Integration</span>
                      </li>
                    </>
                  ) : (

                    <>
                      {location.state?.source !== 'list-upload' && (
                        <>
                          <li className="breadcrumb-item d-flex align-items-center">
                            <span style={{ paddingRight: "4px" }}>
                              <Icon name="mail-icon" size={14} alt="mail-icon" />
                            </span>
                            <a
                              href="/job-module"
                              className="breadcrumb-link"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate('/job-module', {
                                  state: {
                                    clientId: location.state?.clientId,
                                    clientName: location.state?.clientName,
                                    source: location.state?.source,
                                    isAllClients: location.state?.isAllClients
                                  }
                                });
                              }}
                            >
                              Job Lists
                            </a>
                          </li>

                           <li className="breadcrumb-separator">/</li>

                        </>
                      )}
                     
                      <li className="breadcrumb-item">
                        <a
                          href="#"
                          className="breadcrumb-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/job-details/${location.state.jobDetailsCode}`, {
                              state: {
                                source: location.state?.source || 'list-upload',
                                isAllClients: location.state?.isAllClients,
                                activeTab: location.state.returnActiveTab || 'list-summary'
                              }
                            });
                          }}
                        >
                          {location.state.jobDetailsCode} Job Details
                        </a>
                      </li>
                      <li className="breadcrumb-separator">/</li>
                      <li className="breadcrumb-item">
                        <span className="breadcrumb-active">Integration</span>
                      </li>
                    </>
                  )

                ) : passedClientName ? (

                  <>
                    <li className="breadcrumb-item">
                      <a href="/dashboard" className="breadcrumb-link" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
                        Client Select
                      </a>
                    </li>

                    <li className="breadcrumb-separator">/</li>

                    <li className="breadcrumb-item">
                      <a
                        href="/client-dashboard"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/client-dashboard', {
                            state: {
                              clientId: location.state?.clientId,
                              source: 'client-dashboard',
                              isAllClients: false
                            }
                          });
                        }}
                      >
                        Client: {passedClientName}
                      </a>
                    </li>

                    <li className="breadcrumb-separator">/</li>

                    <li className="breadcrumb-item">
                      <span className="breadcrumb-active">Integration</span>
                    </li>
                  </>
                ) : location.state?.source === 'reports' ? (
                  <>
                    <li className="breadcrumb-item">
                      <a href="/reports" className="breadcrumb-link" onClick={(e) => {
                        e.preventDefault();
                        navigate('/reports', {
                          state: {
                            restoreReports: true,
                            reportType: location.state?.reportType || 'Client Results',
                            reportFilters: location.state?.reportFilters || {},
                            rowData: location.state?.rowData || [],
                            selectedClients: location.state?.selectedClients || [],
                            selectedClientIds: location.state?.selectedClientIds || [],
                            searchText: location.state?.searchText || '',
                          }
                        });
                      }}
                      >
                        Reports
                      </a>
                    </li>

                    <li className="breadcrumb-separator">/</li>

                    <li className="breadcrumb-item">
                      <span className="breadcrumb-active">Integration</span>
                    </li>
                  </>
                ) : null}

              </ol>
            </nav>
          </div>


          {/* Main Tabs */}
          <div className="tallies-tabs-simple">

            {!viewOnly && (
              <button
                className={`tallies-tab-simple ${activeTab === 'tallies' ? 'active' : ''}`}
                onClick={() => setActiveTab('tallies')}
              >
                Tallies
              </button>
            )}

            {canViewLists && (
              <button
                className={`tallies-tab-simple ${activeTab === 'lists' ? 'active' : ''}`}
                onClick={() => setActiveTab('lists')}
              >
                List of lists
              </button>
            )}

          </div>
        </div >

        {/* Content */}
        < div className="tallies-sidebar-content" >
          {activeTab === 'tallies' ? (
            <>

              {canUploadTallies && (
                <>
                  <div className='tallies-header'>
                    <span className="tallies-content-title">Upload Tallies File</span>
                  </div>


                  <div className="tallies-filters-container">
                    <div className="tallies-filter-item">
                      <label className="tallies-filter-label">
                        Batch Date <span className="required-field">*</span>
                      </label>
                      <div className={`date-wrapper ${uploadBatchDate ? 'has-value' : ''}`}>
                        <input
                          type="date"
                          value={uploadBatchDate}
                          onChange={handleUploadBatchDateChange}
                          className={`tallies-filter-input ${batchDateError ? 'error' : ''}`}
                        />
                      </div>
                      {batchDateError && (
                        <div className="error-message" style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                          {batchDateError}
                        </div>
                      )}
                    </div>

                    <div className="tallies-filter-item">
                      <label className="tallies-filter-label">
                        Batch Number <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter batch number"
                        value={uploadBatchNumber}
                        onChange={handleUploadBatchNumberChange}
                        className={`tallies-filter-input ${batchNumberError ? 'error' : ''}`}
                        maxLength={50}
                      />
                      {batchNumberError && (
                        <div className="error-message" style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                          {batchNumberError}
                        </div>
                      )}
                    </div>

                    <div className="tallies-filter-item">
                      <label className="tallies-filter-label">
                        Returns <span className="required-field">*</span>
                      </label>
                      <div className="tallies-filter-input-wrapper">
                        <select
                          value={returns}
                          onChange={(e) => setReturns(e.target.value)}
                          className="tallies-filter-select"
                        >
                          <option value="Non-Cumulative">Non-Cumulative</option>
                          <option value="Cumulative">Cumulative</option>
                        </select>
                        <ChevronDown size={16} className="tallies-filter-chevron-icon" />
                      </div>
                    </div>
                  </div>

                  {/* File Upload Section */}
                  <div className="tallies-upload-section">
                    <label className="tallies-upload-label">
                      File Upload <span className="required-field">*</span>
                    </label>
                    {!uploadedFile ? (
                      <div
                        className={`tallies-upload-box ${isDragging ? 'tallies-dragging' : ''} ${fileError ? 'error' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload size={24} className={`tallies-upload-box-icon ${isDragging ? 'tallies-dragging' : ''}`} />
                        <p className="tallies-upload-box-text">
                          Drag and drop Excel (.xlsx or .xls) file here, or{' '}
                          <span className="tallies-browse-text">Browse...</span>
                        </p>
                        <input
                          ref={fileInputRef}
                          id="tallies-file-input"
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileUpload}
                          className="tallies-hidden-input"
                        />
                      </div>
                    ) : (
                      <div className="tallies-uploaded-file-card">
                        <div className="tallies-file-info-inline">
                          <Icon name="package-icon" size={24} />
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <div className="tallies-file-name">{uploadedFile.name}</div>
                            <div className="tallies-file-size">{formatFileSize(uploadedFile.size)}</div>
                          </div>
                        </div>
                        <button
                          className="tallies-file-remove-btn"
                          onClick={handleRemoveFile}
                          title="Remove file"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                    {fileError && (
                      <div className="error-message" style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                        {fileError}
                      </div>
                    )}
                  </div>

                  {/* Download Template Link with Upload Button */}
                  <div className="download-upload-row">
                    <div
                      className="tallies-download-template"
                      onClick={downloadTemplate}
                      style={{ cursor: 'pointer' }}
                    >
                      <Download size={14} className="tallies-download-template-icon" />
                      <span className="tallies-download-template-text underlined-text">Download template</span>
                    </div>

                    {uploadedFile && (
                      <button
                        className={`upload-file-btn ${!isUploadEnabled ? 'disabled' : ''}`}
                        onClick={handleUploadFileToAPI}
                        disabled={!isUploadEnabled || isUploading}
                      >
                        {isUploading ? (
                          <>
                            <span className="loading-spinner">⟳</span>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <span>Upload File</span>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* Batch Tabs with Refresh Button */}
              <div className="tallies-batch-tabs-container">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div className="tallies-batch-tabs">
                    <button
                      className={`tallies-batch-tab ${activeBatchTab === 'pending' ? 'active' : ''}`}
                      onClick={() => setActiveBatchTab('pending')}
                    >
                      <Icon
                        name="pending-icon"
                        size={16}
                        className="tab-icon"
                        style={{ marginRight: '6px' }}
                      />
                      Pending Batches
                    </button>
                    <button
                      className={`tallies-batch-tab ${activeBatchTab === 'successful' ? 'active' : ''}`}
                      onClick={() => setActiveBatchTab('successful')}
                    >
                      <Icon
                        name="successful-icon"
                        size={16}
                        className="tab-icon"
                        style={{ marginRight: '6px' }}
                      />
                      Successful Batches
                    </button>
                  </div>
                </div>

                {/* Loading State */}
                {loadingBatches && (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    <AisScreenLoader />
                  </div>
                )}

                {/* Error State */}
                {batchesError && (
                  <div style={{ color: '#dc2626', padding: '10px', textAlign: 'center' }}>
                    {batchesError}
                  </div>
                )}

                {/* Batch Content */}
                {!loadingBatches && !batchesError && (
                  <div className="tallies-batch-content">
                    {activeBatchTab === 'pending' ? (
                      <div className="tallies-batches-section">

                        <div className="successful-batches-filters" style={{ marginBottom: '16px', display: 'flex', gap: '200px', alignItems: 'flex-end' }}>
                          <div className="tallies-filter-item" style={{ flex: 1 }}>
                            <label className="tallies-filter-label">Select Client</label>
                            <div className="tallies-filter-input-wrapper">
                              <select
                                value={selectedPendingClient}
                                onChange={(e) => setSelectedPendingClient(e.target.value)}
                                className="tallies-filter-select"
                                disabled={loadingClients}
                              >
                                <option value="">All Clients</option>
                                {clientOptions.map(client => (
                                  <option key={client.clientId} value={client.value}>
                                    {client.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown size={16} className="tallies-filter-chevron-icon" />
                            </div>
                          </div>

                          <div className="tallies-filter-item" style={{ flex: 1 }}>
                            <label className="tallies-filter-label">Search Pending Batches</label>
                            <div className="tallies-search-input-wrapper" style={{ position: 'relative' }}>
                              <Search
                                size={16}
                                style={{
                                  position: 'absolute',
                                  left: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  color: '#9ca3af',
                                  zIndex: 1,
                                  pointerEvents: 'none'
                                }}
                              />
                              <input
                                type="text"
                                placeholder="Search by batch number or date"
                                value={searchPendingBatchTerm}
                                onChange={(e) => setSearchPendingBatchTerm(e.target.value)}
                                className="tallies-filter-input"
                                style={{
                                  paddingRight: '32px',
                                  paddingLeft: '32px'
                                }}
                              />
                              {searchPendingBatchTerm && (
                                <X
                                  size={16}
                                  className="tallies-clear-icon"
                                  onClick={clearPendingSearch}
                                  style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    color: '#9ca3af',
                                    zIndex: 1
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="tallies-batches-section">
                          <AgGrid
                            rowData={filteredPendingBatchData}
                            columnDefs={batchColumnDefs}
                            pagination={true}
                            paginationPageSize={10}
                            paginationPageSizeSelector={[10, 20, 50, 100]}
                            height="250px"
                            enableSearch={false}
                            enableSorting={true}
                            enableFilter={true}
                            hideSelectAll={true}
                            hidePivotMode={true}
                            hideColumnMenu={false}
                            hideExportButton={true}
                            hideSideBar={true}
                            getRowId={getRowId}
                            overlayNoRowsTemplate={`
      <span style="padding: 10px;">
        ${searchPendingBatchTerm || selectedPendingClient
                                ? 'No matching records found'
                                : 'No pending batches found'}
      </span>
    `}
                          />
                        </div>

                      </div>


                    ) : (
                      <>

                        <div className="successful-batches-filters" style={{ marginBottom: '16px', display: 'flex', gap: '200px', alignItems: 'flex-end' }}>
                          <div className="tallies-filter-item" style={{ flex: 1 }}>
                            <label className="tallies-filter-label">Select Client</label>
                            <div className="tallies-filter-input-wrapper">
                              <select
                                value={selectedClient}
                                onChange={(e) => setSelectedClient(e.target.value)}
                                className="tallies-filter-select"
                                disabled={loadingClients}
                              >
                                <option value="">All Clients</option>
                                {clientOptions.map(client => (
                                  <option key={client.clientId} value={client.value}>
                                    {client.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown size={16} className="tallies-filter-chevron-icon" />
                            </div>
                          </div>

                          <div className="tallies-filter-item" style={{ flex: 1 }}>
                            <label className="tallies-filter-label">Search Batches</label>
                            <div className="tallies-search-input-wrapper" style={{ position: 'relative' }}>

                              {mailCodeFilterContext ? (

                                <div
                                  style={{
                                    minHeight: '38px',
                                    height: 'auto',
                                    boxSizing: 'border-box',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    padding: '4px 32px 4px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#fff'
                                  }}
                                >
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    backgroundColor: '#e9ecef',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '13px',
                                    gap: '6px'
                                  }}>
                                    {mailCodeFilterContext.batchNumbers.size} batch{mailCodeFilterContext.batchNumbers.size !== 1 ? 'es' : ''} selected
                                    <span
                                      onClick={() => {
                                        setMailCodeFilterContext(null);
                                        setSearchSuccessfulBatchTerm('');
                                      }}
                                      style={{ cursor: 'pointer', fontSize: '14px', color: '#666' }}
                                    >×</span>
                                  </span>
                                </div>
                              ) : (

                                <>
                                  <Search
                                    size={16}
                                    style={{
                                      position: 'absolute', left: '10px', top: '50%',
                                      transform: 'translateY(-50%)', color: '#9ca3af',
                                      zIndex: 1, pointerEvents: 'none'
                                    }}
                                  />
                                  <input
                                    type="text"
                                    placeholder="Search by batch number or date"
                                    value={searchSuccessfulBatchTerm}
                                    onChange={(e) => setSearchSuccessfulBatchTerm(e.target.value)}
                                    className="tallies-filter-input"
                                    style={{ paddingRight: '32px', paddingLeft: '32px' }}
                                  />
                                  {searchSuccessfulBatchTerm && (
                                    <X
                                      size={16}
                                      className="tallies-clear-icon"
                                      onClick={clearSuccessfulSearch}
                                      style={{
                                        position: 'absolute', right: '8px', top: '50%',
                                        transform: 'translateY(-50%)', cursor: 'pointer',
                                        color: '#9ca3af', zIndex: 1
                                      }}
                                    />
                                  )}
                                </>
                              )}

                            </div>
                          </div>
                        </div>

                        <div className="tallies-batches-section">
                          <AgGrid
                            rowData={filteredSuccessfulBatchData}
                            columnDefs={batchColumnDefs}
                            pagination={true}
                            paginationPageSize={10}
                            paginationPageSizeSelector={[10, 20, 50]}
                            height="250px"
                            enableSearch={false}
                            enableSorting={true}
                            enableFilter={true}
                            hideSelectAll={true}
                            hidePivotMode={true}
                            hideColumnMenu={false}
                            hideExportButton={true}
                            hideSideBar={true}
                            getRowId={getRowId}
                            overlayNoRowsTemplate={`
      <span style="padding: 10px;">
        ${searchSuccessfulBatchTerm || selectedClient
                                ? 'No matching records found'
                                : 'No successful batches found'}
      </span>
    `}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>


              <div className="tallies-batch-details-section">
                <div className='tallies-header'>
                  <span className="tallies-content-title">Batch Details</span>
                </div>

                <div className="tallies-batch-details-content">

                  {!hasSelectedBatch ? (
                    <div>
                      <span className='no-batch-selected'> No batch selected. Use the  <Icon
                        name="eye"
                        size={16}
                      /> in the batch list above to view its mail codes.</span>
                    </div>
                  ) : (

                    <>
                      <div className="tallies-batch-details-row">
                        <div className="tallies-batch-date-container">

                          <div className="tallies-filter-item batch-date-field-item">
                            <label className="tallies-filter-label">Batch Date</label>
                            <div className="tallies-filter-input-wrapper">
                              <input
                                type="date"
                                placeholder="MM/DD/YYYY"
                                value={viewBatchDate}
                                onChange={handleViewBatchDateChange}
                                className={`tallies-filter-input ${batchDateError ? 'error' : ''} ${(!isEditAllowed || isManageMailCodesActive) ? 'disabled' : ''}`}
                                pattern="\d{4}-\d{2}-\d{2}"
                                disabled={!isEditAllowed || isManageMailCodesActive}
                              />
                            </div>
                            <div className="error-message-wrapper" style={{ minHeight: '20px' }}>
                              {batchDateError && isEditAllowed && (
                                <div className="error-message" style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                                  {batchDateError}
                                </div>
                              )}
                            </div>
                          </div>


                          <div className="tallies-filter-item batch-number-field-item">
                            <label className="tallies-filter-label">Batch Number</label>
                            <input
                              type="text"
                              value={viewBatchNumber}
                              onChange={handleViewBatchNumberChange}
                              className={`tallies-filter-input ${batchNumberError ? 'error' : ''} ${(!isEditAllowed || isManageMailCodesActive) ? 'disabled' : ''}`}
                              maxLength={50}
                              disabled={!isEditAllowed || isManageMailCodesActive}
                            />
                            <div className="error-message-wrapper" style={{ minHeight: '20px' }}>
                              {batchNumberError && isEditAllowed && (
                                <div className="error-message" style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                                  {batchNumberError}
                                </div>
                              )}
                            </div>
                          </div>


                          {isEditAllowed && (
                            <div className="batch-save-btn-wrapper">
                              <button
                                className="tallies-batch-save-btn"
                                onClick={handleSave}
                                disabled={!viewBatchDate || batchDateError || !viewBatchNumber || batchNumberError || isDeleting || isManageMailCodesActive}
                              >
                                <span>{isDeleting ? 'Saving...' : 'Save'}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>


                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
                        <div className="left-side-buttons">
                          {!isManageMailCodesActive && canEditTallies && (
                            <button
                              className="manage-mail-codes-btn tallies-manage-btn"
                              onClick={handleManageMailCodesClick}
                              disabled={isDeleting || loadingListSummary}
                            >
                              Manage Mail Codes
                            </button>
                          )}
                        </div>

                        <div className="job-details-menu-container">
                          {isManageMailCodesActive && (
                            <div className="action-buttons d-flex gap-2" style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className="delete-btn btn btn-danger btn-sm"
                                onClick={handleDeleteClick}
                                disabled={isDeleting || selectedMailCodeRows.length === 0}
                              >
                                {isDeleting ? 'Deleting...' : `Delete Selected (${selectedMailCodeRows.length})`}
                              </button>

                              {isEditAllowed && (
                                <button
                                  className="save-changes-btn"
                                  onClick={handleSaveChangesClick}
                                  disabled={isDeleting}
                                >
                                  Save Changes
                                </button>
                              )}

                              <button
                                className="cancel-btn btn-sm"
                                onClick={handleCancelClick}
                                disabled={isDeleting}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>


                      <div className="tallies-mail-codes-table-container">
                        {loadingListSummary ? (
                          <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#666',
                            background: '#f9f9f9',
                            borderRadius: '4px',
                            minHeight: '200px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div>
                              <div style={{ marginBottom: '10px' }}>Loading batch details...</div>
                              <AisScreenLoader />
                            </div>
                          </div>
                        ) : (
                          <AgGrid
                            rowData={rowDataWithAddRow}
                            pinnedBottomRowData={pinnedBottomRowData}
                            columnDefs={mailCodeColumnDefs}
                            paginationPageSizeSelector={[10, 20, 50]}
                            height="250px"
                            enableSearch={false}
                            enableSorting={true}
                            enableFilter={true}
                            hideSelectAll={true}
                            hidePivotMode={true}
                            hideColumnMenu={false}
                            hideExportButton={true}
                            hideSideBar={true}
                            rowSelection={isManageMailCodesActive ? 'multiple' : undefined}
                            onSelectionChanged={handleMailCodeSelectionChanged}
                            suppressRowClickSelection={true}
                            getRowId={getRowId}
                            onGridReady={handleGridReady}
                            onCellClicked={handleCellClicked}
                            onCellValueChanged={handleCellValueChanged}
                            onCellEditingStopped={handleCellEditingStopped}
                            onRowValueChanged={handleRowValueChanged}
                            gridOptions={{
                              defaultColDef: {
                                resizable: true,
                                sortable: true,
                                filter: true
                              },
                              editType: 'fullRow',
                              suppressRowClickSelection: false,
                              getRowStyle: (params) => {
                                if (params.node.rowPinned === 'bottom') {
                                  return {
                                    backgroundColor: '#f0f8ff',
                                    fontWeight: 'bold',
                                    borderTop: '0.5px solid #babfc7'
                                  };
                                }
                                if (params.data?.isTotalsRow) {
                                  return {
                                    backgroundColor: '#f0f8ff',
                                    fontWeight: 'bold'
                                  };
                                }

                                // Check if status exists and is not "Success"
                                if (params.data?.status) {
                                  const cleanStatus = params.data.status.replace(/\?+$/, '').trim();

                                  if (cleanStatus !== 'Success') {
                                    return {
                                      backgroundColor: '#FFEAEC',
                                      color: '#000'
                                    };
                                  }
                                }

                                return null;
                              }
                            }}
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (

            <div className="tallies-empty-state">
              <ListOfList />
            </div>
          )}
        </div >


        < style jsx > {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .loading-spinner {
          display: inline-block;
          animation: spin 1s linear infinite;
        }

        input:disabled,
        input.disabled {
          background-color: #f5f5f5;
          cursor: not-allowed;
          opacity: 0.7;
        }
        
        button:disabled,
        button.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .tallies-filter-input.disabled {
          background-color: #f5f5f5;
          cursor: not-allowed;
        }
        
        .tallies-search-input-wrapper {
          position: relative;
        }
        
        .tallies-clear-icon:hover {
          color: #dc2626 !important;
        }
        
        /* Batch Details Section Layout Fix */
        .tallies-batch-details-row {
          margin-bottom: 8px;
        }

        .tallies-batch-date-container {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .batch-date-field-item,
        .batch-number-field-item {
          flex: 1;
          min-width: 200px;
          margin-bottom: 0;
        }

        .batch-save-btn-wrapper {
          margin-top: 28px;
          flex-shrink: 0;
        }

        /* Fixed height for error messages to prevent layout shift */
        .error-message-wrapper {
          min-height: 20px;
          transition: none;
        }

        .tallies-batch-save-btn {
          padding: 10px 32px;
          background-color: #01467D;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .tallies-batch-save-btn:hover:not(:disabled) {
          background-color: #1e4f6f;
        }

        .tallies-batch-save-btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        /* ✅ Pinned bottom row (totals) styling — make text bold across the row */
        :global(.ag-row-pinned),
        :global(.ag-row-pinned .ag-cell),
        :global(.ag-floating-bottom),
        :global(.ag-floating-bottom .ag-cell) {
          font-weight: bold !important;
          background-color: #f0f8ff !important;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .tallies-batch-date-container {
            flex-direction: column;
            gap: 16px;
          }
          
          .batch-save-btn-wrapper {
            margin-top: 0;
            width: 100%;
          }
          
          .batch-save-btn-wrapper .tallies-batch-save-btn {
            width: 100%;
          }
          
          .batch-date-field-item,
          .batch-number-field-item {
            width: 100%;
          }
        }

        /* Ensure consistent height for input fields */
        .tallies-filter-input {
          height: 38px;
          box-sizing: border-box;
        }

        .tallies-filter-input-wrapper {
          height: 38px;
        }

        .tallies-filter-item {
          margin-bottom: 0;
        }
      `}</style >
      </div >

      {showDeleteConfirm && (
        <div className="delete-confirmation-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="delete-confirmation-modal" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginBottom: '15px' }}>Confirm Delete</h3>

            <p style={{ marginBottom: '20px' }}>
              {deleteContext?.tallyIds?.length > 0
                ? `Are you sure you want to delete ${deleteContext.tallyIds.length} selected row(s)?`
                : `Are you sure you want to delete batch ${deleteContext?.batchData?.batchNumber}?`
              }
            </p>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer'
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )
      }

    </>
  );
};

export default TalliesSidebar;